import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import {
    getRouteOrdersView,
    getRouteReportsSavingsSummary,
} from 'shared/const/router';
import { formatReportCurrency } from 'shared/lib/reportFormatters';
import { BackButton } from 'shared/ui/BackButton';
import { ReportMetric } from 'shared/ui/ReportMetric/ReportMetric';
import { DynamicTooltip } from 'shared/ui/Tooltip';
import {
    ComposableReportWidget,
    ReportWidgetInteractionPayload,
} from 'widgets/ComposableReportWidget';
import { Page } from 'widgets/Page';

import { ProductDrillInContextHeader } from './ProductDrillInContextHeader';

import { useReportsPageSavingsSummaryProduct } from '../model/hooks/useReportsPageSavingsSummaryProduct';
import { getProductOrdersWidgetConfig } from '../model/lib/getProductOrdersWidgetConfig';
import { mapProductOrdersToWidgetItems } from '../model/lib/mapProductOrdersToWidgetItems';

const ReportsPageSavingsSummaryProduct = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        sku,
        product: productFromState,
        fromMissedSavings,
        loading,
        totalSpent,
        totalQuantity,
        ordersCount,
        summaryAmount,
        bestPriceAmount,
        summaryPercentLabel,
        primaryMetricColor,
        lastRequestParamsPresent,
        ordersData,
        canLoadMoreOrders,
        loadMoreOrders,
    } = useReportsPageSavingsSummaryProduct();

    const handleBack = useCallback(() => {
        navigate(getRouteReportsSavingsSummary(), {
            state: { fromDrillIn: true },
        });
    }, [navigate]);

    const openOrderDetailsInNewTab = useCallback((orderId: string) => {
        window.open(
            getRouteOrdersView(orderId),
            '_blank',
            'noopener,noreferrer',
        );
    }, []);

    const handleOrderDetailsClick = useCallback(
        (payload: ReportWidgetInteractionPayload) => {
            const orderId = payload.item.meta?.orderId;
            if (typeof orderId === 'string') {
                openOrderDetailsInNewTab(orderId);
            }
        },
        [openOrderDetailsInNewTab],
    );

    if (!sku || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSavingsSummary()} replace />;
    }

    return (
        <Page
            dataTestId="reports-page-savings-summary-product"
            padding="24px 35px"
        >
            <Box>
                <BackButton
                    onBack={handleBack}
                    title={t('Savings Summary')}
                    id="back-to-savings-summary-button"
                />

                <ProductDrillInContextHeader
                    sku={sku}
                    product={productFromState}
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        marginBottom: 3,
                    }}
                >
                    <ReportMetric
                        title={t(
                            fromMissedSavings
                                ? 'savingsSummary.missedSavings'
                                : 'savingsSummary.productsMetricTotalSavings',
                            fromMissedSavings
                                ? 'Missed Savings'
                                : 'Total Savings',
                        )}
                        primary={formatReportCurrency(summaryAmount)}
                        primaryColor={primaryMetricColor}
                        primaryTooltip={t(
                            fromMissedSavings
                                ? 'savingsSummary.missedSavingsTooltip'
                                : undefined,
                        )}
                        loading={loading}
                        minHeight="172px"
                    >
                        <Box
                            sx={{
                                fontSize: '12px',
                                lineHeight: '16px',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                }}
                            >
                                <span>
                                    {t(
                                        'savingsSummary.competitiveSpend',
                                        'Competitive Spend',
                                    )}
                                </span>
                                <DynamicTooltip
                                    content={t(
                                        'savingsSummary.competitiveSpendTooltip',
                                    )}
                                    containerStyle={{
                                        height: '16px',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <InfoOutlinedIcon
                                        sx={{
                                            fontSize: 18,
                                            color: '#B7B7B7',
                                        }}
                                        data-pdf-tooltip="true"
                                    />
                                </DynamicTooltip>
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: 600,
                                }}
                            >
                                {formatReportCurrency(totalSpent)}
                            </Box>
                        </Box>
                        {fromMissedSavings && (
                            <Box
                                sx={{
                                    fontSize: '12px',
                                    lineHeight: '16px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginTop: 0.5,
                                }}
                            >
                                <span>
                                    {t(
                                        'savingsSummary.missedSavingsColumnBestPrice',
                                        'Best Price',
                                    )}
                                </span>
                                <Box
                                    component="span"
                                    sx={{
                                        fontVariantNumeric: 'tabular-nums',
                                        fontWeight: 600,
                                    }}
                                >
                                    {formatReportCurrency(bestPriceAmount)}
                                </Box>
                            </Box>
                        )}
                    </ReportMetric>
                    <ReportMetric
                        title={t(
                            fromMissedSavings
                                ? 'savingsSummary.percentOfMissedSavings'
                                : 'savingsSummary.percentOfTotalSavings',
                            fromMissedSavings
                                ? '% of Missed Savings'
                                : '% of Total Savings',
                        )}
                        primary={summaryPercentLabel || '-'}
                        primaryColor={primaryMetricColor}
                        loading={loading}
                        minHeight="172px"
                    />
                    <ReportMetric
                        title={t(
                            'savingsSummary.productsMetricTotalQuantity',
                            'Total Quantity',
                        )}
                        primary={totalQuantity.toLocaleString()}
                        loading={loading}
                        minHeight="172px"
                    />
                    <ReportMetric
                        title={t(
                            'savingsSummary.productsMetricOrders',
                            'Orders',
                        )}
                        primary={ordersCount.toLocaleString()}
                        loading={loading}
                        minHeight="172px"
                    />
                </Box>

                <Box sx={{ marginTop: 4 }}>
                    <ComposableReportWidget
                        config={getProductOrdersWidgetConfig({
                            t,
                            fromMissedSavings,
                            canLoadMoreOrders,
                            loadMoreOrders,
                            onOrderDetailsClick: handleOrderDetailsClick,
                        })}
                        items={mapProductOrdersToWidgetItems(
                            ordersData?.items ?? [],
                            fromMissedSavings,
                        )}
                        loading={loading}
                    />
                </Box>
            </Box>
        </Page>
    );
};

export default ReportsPageSavingsSummaryProduct;
